import { confirm, alert } from 'devextreme/ui/dialog';
import { MESSAGES } from '../../../../../../common/Messages';
import { CheckShipmentDays } from '../../../../../../enum/Enum';

// WM shipment expiration / warranty check (DEV-4326, DEV-366).
//
// Delphi evaluates this as each checkbox is toggled and clears the tick when the row is refused:
// uWMD0107.pas:487-522 for the sublot-level mode, and CheckShipExpiration at :1432-1508 for the
// lot-level mode. Select All runs the same handler per row (uWMD0107.pas:410-412), so a bulk select of
// several offending rows does prompt once per row - that is the legacy behaviour.
//
// Despite its name WARRANTYMONTHS_SHIPMENT is subtracted as DAYS: Delphi does
// `expiry - StrToInt(FWarrantyMonths)` against a TDate and its own dialog reads "Minimum Required
// Ship Days" (uWMD0107.pas:495, :502).

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** True when the check does not apply to this item, so no per-row work is needed. */
export function isShipExpirationCheckDisabled(row, expectedFlag) {
    if (!row) return true;
    if (row.WarrantyFlagShipment !== expectedFlag) return true;
    return row.CheckShipmentDays === CheckShipmentDays.Off;
}

/** '2' lets the user override with a Yes/No; anything else bar '1' is a hard stop. */
export function isShipExpirationOverridable(row) {
    return row?.CheckShipmentDays === CheckShipmentDays.CheckAndAllowOverride;
}

/**
 * Builds the violation message for one row, or returns null when the row is acceptable.
 * A row with no expiration date on file is acceptable, matching Delphi's `if not VarIsNull(...)` guard.
 */
export function getShipExpirationViolation(rowLabel, expirationDate, shipDate, requiredShipDays) {
    if (!expirationDate || !shipDate) return null;

    const expiry = new Date(expirationDate);
    const ship = new Date(shipDate);
    if (isNaN(expiry.getTime()) || isNaN(ship.getTime())) return null;

    const lastShipDate = new Date(expiry.getTime() - (requiredShipDays ?? 0) * MS_PER_DAY);
    if (ship.getTime() <= lastShipDate.getTime()) return null;

    const asDate = (d) => d.toLocaleDateString();
    return MESSAGES.SHIP_EXPIRATION_VIOLATION
        .replace('{0}', rowLabel ?? '')
        .replace('{1}', asDate(ship))
        .replace('{2}', asDate(expiry))
        .replace('{3}', requiredShipDays ?? 0)
        .replace('{4}', asDate(lastShipDate));
}

/**
 * Applies the check to the rows just ticked and returns the keys that must be un-ticked, so the caller
 * can `deselectRows` them exactly as Delphi sets `Selected := 0`.
 *
 * @param rows          the newly selected row objects
 * @param keyOf         maps a row to its grid key
 * @param labelOf       maps a row to the code shown in the message
 * @param expiryOf      maps a row to the expiration date the warranty flag selects
 * @param shipDate      the transaction's ship date
 * @param expectedFlag  which WARRANTYFLAG_SHIPMENT arm this screen implements
 */
export async function collectShipExpirationRejections({
    rows, keyOf, labelOf, expiryOf, shipDate, expectedFlag,
}) {
    const rejected = [];

    for (const row of rows ?? []) {
        if (isShipExpirationCheckDisabled(row, expectedFlag)) continue;

        const violation = getShipExpirationViolation(
            labelOf(row), expiryOf(row), shipDate, row.WarrantyMonthsShipment);

        if (!violation) continue;

        if (isShipExpirationOverridable(row)) {
            // eslint-disable-next-line no-await-in-loop
            const proceed = await confirm(
                `<i>${violation}<br/><br/>${MESSAGES.SHIP_EXPIRATION_CONTINUE_QUESTION}</i>`,
                MESSAGES.CONFIRM
            );
            if (!proceed) rejected.push(keyOf(row));
        } else {
            // eslint-disable-next-line no-await-in-loop
            await alert(violation, MESSAGES.ERROR);
            rejected.push(keyOf(row));
        }
    }

    return rejected;
}
